import { useEffect } from 'react';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords?: string;
  canonicalUrl?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  ogType?: string;
  jsonLd?: Record<string, unknown>;
}

export default function SEOHead({
  title,
  description,
  keywords,
  canonicalUrl,
  ogTitle,
  ogDescription,
  ogUrl,
  ogType = 'website',
  jsonLd,
}: SEOHeadProps) {
  useEffect(() => {
    // Document title
    document.title = title;

    // Meta description
    const setOrCreateMetaName = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    // Meta property (Open Graph)
    const setOrCreateMetaProperty = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('property', property);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setOrCreateMetaName('description', description);

    if (keywords) {
      setOrCreateMetaName('keywords', keywords);
    }

    // Canonical URL
    if (canonicalUrl) {
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', canonicalUrl);
    }

    // Open Graph
    if (ogTitle) setOrCreateMetaProperty('og:title', ogTitle);
    if (ogDescription) setOrCreateMetaProperty('og:description', ogDescription);
    if (ogUrl) setOrCreateMetaProperty('og:url', ogUrl);
    setOrCreateMetaProperty('og:type', ogType);

    // Twitter Card
    setOrCreateMetaName('twitter:card', 'summary_large_image');
    if (ogTitle) setOrCreateMetaName('twitter:title', ogTitle);
    if (ogDescription) setOrCreateMetaName('twitter:description', ogDescription);

    // JSON-LD
    let jsonLdScript: HTMLScriptElement | null = null;
    if (jsonLd) {
      // Remove any previously injected JSON-LD (identified by data-seo attribute)
      const prev = document.querySelector('script[data-seo="dynamic"]');
      if (prev) prev.remove();

      jsonLdScript = document.createElement('script');
      jsonLdScript.type = 'application/ld+json';
      jsonLdScript.setAttribute('data-seo', 'dynamic');
      jsonLdScript.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(jsonLdScript);
    }

    return () => {
      // Cleanup on unmount to avoid stale data
      if (jsonLdScript && jsonLdScript.parentNode) {
        jsonLdScript.remove();
      }
    };
  }, [title, description, keywords, canonicalUrl, ogTitle, ogDescription, ogUrl, ogType, jsonLd]);

  return null;
}